import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import scrollReducer from "./actions/layout/scroll";
import deviceReducer from "./actions/layout/device";
import audioReducer from "./actions/audio/audioSlice";
import presenceReducer from "./presence/presenceSlice";
import userDirectoryReducer from "./userDirectory/userDirectorySlice";
import { mileageApi } from "./mileages/mileage";
import {
  userApi,
  signinApi,
} from "./accounts/authuser";
import { mcpApiKeysApi } from "./accounts/mcpApiKeys";
import { membershipsApi } from "./accounts/memberships";
import { meScheduleApi } from "./accounts/meSchedule";
import { projectsApi } from "./services/projects";
import { shotsApi } from "./services/shots";
import { shotCommentsApi } from "./services/shotComments";
import { mediaItemsApi } from "./services/mediaItems";
import { projectMessagesApi } from "./services/projectMessages";
import { uploadApi } from "./services/uploadApi";
import { invitationsApi, publicInvitationsApi } from "./services/invitations";
import { publicCreatorsApi } from "./services/publicCreators";
import { creatorProfileApi } from "./services/creatorProfile";
import { galleryApi } from "./services/gallery";
import { passwordResetApi } from "./services/passwordReset";
import { acrFilescanningApi } from "./services/acrFilescanning";
import { mediaTranscriptionApi } from "./services/mediaTranscription";
import { dmThreadsApi } from "./services/dmThreads";
import { dmMessagesApi } from "./services/dmMessages";
import { chatThreadsApi } from "./services/chatThreads";
import { chatMessagesApi } from "./services/chatMessages";
import { paymentRequestsApi } from "./services/paymentRequests";
import { paymentStatementsApi } from "./services/paymentStatements";
import { transactionsApi } from "./services/transactions";
import { depositApi } from "./services/deposit";
import { creditsApi } from "./services/credits";
import { followsApi } from "./services/follows";
import { rolesApi } from "./services/roles";
import { usersApi } from "./services/users";
import { offersApi } from "./services/offers";
import { taskBoardApi } from "./services/taskBoard";
import { toolPresetsApi } from "./services/toolPresets";
import { imageBoardApi } from "./services/imageBoard";
import { notesApi } from "./services/notes";
import { libraryApi } from "./services/library";
import { accountsApi } from "./services/accounts";
import { adminBalanceApi } from "./services/adminBalance";
import { adminAcrApi } from "./services/adminAcr";
import { adminAiApi } from "./services/adminAi";
import { projectAcrApi } from "./services/projectAcr";
import { aiTextApi } from "./services/aiText";
import { aiImageApi } from "./services/aiImage";
import { soremonApi } from "./services/soremon";
import { demoApi } from "./services/demo";
import { adminGalleryTagsApi } from "./services/adminGalleryTags";
import { adminProjectsApi } from "./services/adminProjects";
import { adminMileageRanksApi } from "./services/adminMileageRanks";
import { githubApi } from "./services/github";
import offerWizardReducer from "./offerWizard/offerWizardSlice";

export const store = configureStore({
  reducer: {
    layout: scrollReducer,
    device: deviceReducer,
    audio: audioReducer,
    presence: presenceReducer,
    userDirectory: userDirectoryReducer,
    [mileageApi.reducerPath]: mileageApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [signinApi.reducerPath]: signinApi.reducer,
    [mcpApiKeysApi.reducerPath]: mcpApiKeysApi.reducer,
    [membershipsApi.reducerPath]: membershipsApi.reducer,
    [meScheduleApi.reducerPath]: meScheduleApi.reducer,
    [projectsApi.reducerPath]: projectsApi.reducer,
    [shotsApi.reducerPath]: shotsApi.reducer,
    [shotCommentsApi.reducerPath]: shotCommentsApi.reducer,
    [mediaItemsApi.reducerPath]: mediaItemsApi.reducer,
    [projectMessagesApi.reducerPath]: projectMessagesApi.reducer,
    [uploadApi.reducerPath]: uploadApi.reducer,
    [invitationsApi.reducerPath]: invitationsApi.reducer,
    [publicInvitationsApi.reducerPath]: publicInvitationsApi.reducer,
    [publicCreatorsApi.reducerPath]: publicCreatorsApi.reducer,
    [creatorProfileApi.reducerPath]: creatorProfileApi.reducer,
    [galleryApi.reducerPath]: galleryApi.reducer,
    [passwordResetApi.reducerPath]: passwordResetApi.reducer,
    [acrFilescanningApi.reducerPath]: acrFilescanningApi.reducer,
    [mediaTranscriptionApi.reducerPath]: mediaTranscriptionApi.reducer,
    [dmThreadsApi.reducerPath]: dmThreadsApi.reducer,
    [dmMessagesApi.reducerPath]: dmMessagesApi.reducer,
    [chatThreadsApi.reducerPath]: chatThreadsApi.reducer,
    [chatMessagesApi.reducerPath]: chatMessagesApi.reducer,
    [paymentRequestsApi.reducerPath]: paymentRequestsApi.reducer,
    [paymentStatementsApi.reducerPath]: paymentStatementsApi.reducer,
    [transactionsApi.reducerPath]: transactionsApi.reducer,
    [depositApi.reducerPath]: depositApi.reducer,
    [creditsApi.reducerPath]: creditsApi.reducer,
    [followsApi.reducerPath]: followsApi.reducer,
    [rolesApi.reducerPath]: rolesApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [offersApi.reducerPath]: offersApi.reducer,
    [taskBoardApi.reducerPath]: taskBoardApi.reducer,
    [toolPresetsApi.reducerPath]: toolPresetsApi.reducer,
    [imageBoardApi.reducerPath]: imageBoardApi.reducer,
    [notesApi.reducerPath]: notesApi.reducer,
    [libraryApi.reducerPath]: libraryApi.reducer,
    [accountsApi.reducerPath]: accountsApi.reducer,
    [adminBalanceApi.reducerPath]: adminBalanceApi.reducer,
    [adminAcrApi.reducerPath]: adminAcrApi.reducer,
    [adminAiApi.reducerPath]: adminAiApi.reducer,
    [projectAcrApi.reducerPath]: projectAcrApi.reducer,
    [aiTextApi.reducerPath]: aiTextApi.reducer,
    [aiImageApi.reducerPath]: aiImageApi.reducer,
    [soremonApi.reducerPath]: soremonApi.reducer,
    [demoApi.reducerPath]: demoApi.reducer,
    [adminGalleryTagsApi.reducerPath]: adminGalleryTagsApi.reducer,
    [adminProjectsApi.reducerPath]: adminProjectsApi.reducer,
    [adminMileageRanksApi.reducerPath]: adminMileageRanksApi.reducer,
    [githubApi.reducerPath]: githubApi.reducer,
    offerWizard: offerWizardReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      mileageApi.middleware,
      userApi.middleware,
      signinApi.middleware,
      mcpApiKeysApi.middleware,
      membershipsApi.middleware,
      meScheduleApi.middleware,
      projectsApi.middleware,
      shotsApi.middleware,
      shotCommentsApi.middleware,
      mediaItemsApi.middleware,
      projectMessagesApi.middleware,
      uploadApi.middleware,
      invitationsApi.middleware,
      publicInvitationsApi.middleware,
      publicCreatorsApi.middleware,
      creatorProfileApi.middleware,
      galleryApi.middleware,
      passwordResetApi.middleware,
      acrFilescanningApi.middleware,
      mediaTranscriptionApi.middleware,
      dmThreadsApi.middleware,
      dmMessagesApi.middleware,
      chatThreadsApi.middleware,
      chatMessagesApi.middleware,
      paymentRequestsApi.middleware,
      paymentStatementsApi.middleware,
      transactionsApi.middleware,
      depositApi.middleware,
      creditsApi.middleware,
      followsApi.middleware,
      rolesApi.middleware,
      usersApi.middleware,
      offersApi.middleware,
      taskBoardApi.middleware,
      toolPresetsApi.middleware,
      imageBoardApi.middleware,
      notesApi.middleware,
      libraryApi.middleware,
      accountsApi.middleware,
      adminBalanceApi.middleware,
      adminAcrApi.middleware,
      adminAiApi.middleware,
      projectAcrApi.middleware,
      aiTextApi.middleware,
      aiImageApi.middleware,
      soremonApi.middleware,
      demoApi.middleware,
      adminGalleryTagsApi.middleware,
      adminProjectsApi.middleware,
      adminMileageRanksApi.middleware,
      githubApi.middleware,
    ),
});

setupListeners(store.dispatch);
